import {isEqual, isString} from 'lodash';
import {createSelector} from 'reselect';

import {
  isFilterValueNotEmpty,
  mapFiltersToTerms,
  mapTermsToFilters,
} from '../../../../analytics/helpers/analyticsSavedViewsHelpers';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {getAccounts} from '../../../data/dataSelectors';
import {getTeams} from '../../../data/teams/teamsSelectors';
import {getAnalyticsPanel} from '../analyticsPanelSelectors';
import {getAnalyticsSelectedViewId} from '../parameters/analyticsPanelParametersSelectors';
import {getAnalyticsReportOptions} from '../report/analyticsPanelReportSelectors';
import {getAnalyticsSelectedView, getAnalyticsViewsAllAccounts} from '../views/analyticsPanelViewsSelectors';
import {isAnalyticsViewSaved} from '../views/analyticsPanelViewsState';
import {
  ANALYTICS_FILTERS_TAG_MODE,
  ANALYTICS_FILTERS_TAG_RELATION,
  DEFAULT_FILTERS_STATE,
  FiltersStateKey,
} from './analyticsPanelFiltersState';

export const getAnalyticsFilters = createSelector(getAnalyticsPanel, ({filters}) => filters);

export const getHasDiffFilters = createSelector(
  [getAnalyticsSelectedView, getAnalyticsFilters, getAnalyticsSelectedViewId, getAnalyticsReportOptions],
  (selectedView, filters, selectedViewId, reportOptions) => {
    const originalFilters = !selectedView ? DEFAULT_FILTERS_STATE : mapTermsToFilters(selectedView.filters);
    // We compare terms rather than filters state here because the terms treat a
    // filter being set to undefined the same as a filter set to an empty array, which
    // isn't always the case in the filters state and could lead to inconsistencies if
    // we tried to compare the state instead.
    const hasDiffFilters = isAnalyticsViewSaved(selectedViewId)
      ? !isEqual(mapFiltersToTerms(originalFilters), mapFiltersToTerms(filters))
      : Object.values(filters).some(isFilterValueNotEmpty);

    const hasDiffOptions = isAnalyticsViewSaved(selectedViewId)
      ? !isEqual(reportOptions, selectedView?.filters.options)
      : Object.keys(reportOptions).length > 0;

    return {hasDiffFilters: hasDiffFilters || hasDiffOptions};
  },
);

export const makeGetAnalyticsFilter = <T extends FiltersStateKey>(type: T) =>
  createSelector(getAnalyticsPanel, ({filters}) => filters[type]);

export const getAnalyticsFilterTagRelation = createSelector(
  getAnalyticsPanel,
  ({filters}) => filters[ANALYTICS_FILTERS_TAG_RELATION],
);

export const getAnalyticsFilterTagMode = createSelector(
  getAnalyticsPanel,
  ({filters}) => filters[ANALYTICS_FILTERS_TAG_MODE],
);

export const getAnalyticsFilterAllAccounts = createSelector(
  [getAnalyticsViewsAllAccounts, getAccounts],
  (analyticsViewsAccounts, accounts) => ({
    ...accounts,
    ...analyticsViewsAccounts,
  }),
);

export const getAnalyticsTagFilters = createSelector(getAnalyticsPanel, ({filters}) => ({
  [ANALYTICS_FILTERS_TAG_MODE]: filters[ANALYTICS_FILTERS_TAG_MODE],
  [ANALYTICS_FILTERS_TAG_RELATION]: filters[ANALYTICS_FILTERS_TAG_RELATION],
  [AnalyticsFilterTypesEnum.TAGS]: filters[AnalyticsFilterTypesEnum.TAGS],
}));

export const getAnalyticsExcludeTagFilters = createSelector(getAnalyticsPanel, ({filters}) => ({
  [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: filters[AnalyticsFilterTypesEnum.EXCLUDE_TAGS],
  [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: filters[AnalyticsFilterTypesEnum.WITHOUT_TAGS],
}));

/** Retrieve the namespaces of current selections in the teams filter.  */
export const getSelectedTeamNamespaces = createSelector(
  getTeams,
  makeGetAnalyticsFilter(AnalyticsFilterTypesEnum.TEAMS),
  (teams, teamFilterIds) => {
    if (!teamFilterIds) {
      return new Set<string>();
    }

    const namespaces = teamFilterIds.map((teamId) => teams[teamId]?.namespace).filter(isString);
    return new Set(namespaces);
  },
);
